import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Checkbox,
  TextField,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import PowerButton from './shared/PowerButton';
import DeviceTypeImage from './shared/DeviceTypeImage';
import { DeviceIconWithBrandChip } from './shared/DeviceBrandChip';
import ReconnectingDots from './shared/ReconnectingDots';
import TimerCountdown from './shared/TimerCountdown';
import { useTimerProgress } from '../hooks/useTimerProgress';
import { shouldShowDeviceTimer, formatTimerBadgeTooltip } from '../utils/timerUtils';
import { registerComponent, unregisterComponent, isUiScrolling } from '../utils/componentUpdateRegistry';
import {
  Bolt,
  Brightness6,
  ArrowUpward,
  ArrowDownward,
  Pause,
  BlindsClosedTwoTone,
  BlindsTwoTone,
  Timer,
  CalendarMonth,
  DragIndicator,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Lock,
  VideoCameraFront,
} from '@mui/icons-material';
import { POWER_BUTTON_SIZE, ANIMATION_DURATION } from '../styles/deviceStyles';
import { eventBus } from '../services/EventBus';
import { isHeatSupplyFrostHolding } from '../services/evohomeState';
import GroupIconsRow from './GroupIconsRow';
import { mapaDispositivos } from '../services/consumptionService';
import { getCanControlDevicesCached } from '../services/permissionsService';
import { getDemoRestrictionSnackbarProps } from '../constants/demoRestrictions';
import { getListenerPool } from '../utils/heavyInitializations';
import { useAiDevicePolicy, useShowAiCardBadge } from '../contexts/AiDevicePolicyContext';
import { deviceBadgesChanged } from '../utils/deviceBadgeCompare';

// Función para determinar el color según el valor de consumo - EXACTO como en App.tsx
const getColorForConsumo = (consumo?: number): string => {
  if (consumo === undefined || consumo === null) return '#888888';
  return consumo >= 0 ? '#2391FF' : '#00ff00';
};

// Función para formatear consumo (W o kW) - EXACTO como en App.tsx
const formatConsumption = (watts: number): string => {
  if (Math.abs(watts) >= 1000) {
    return `${(watts / 1000).toFixed(2)} kW`;
  }
  return `${watts.toFixed(2)} W`;
};

// Función de mapeo para adaptar los valores del estado del cover a los que espera la lógica avanzada
const mapCoverStateToBackend = (state: string): 'open' | 'closed' | 'opening' | 'closing' | 'stopped' | 'unknown' => {
  // Si el estado es booleano, convertirlo a string
  if (typeof state === 'boolean') {
    return state ? 'open' : 'closed';
  }
  
  switch(state) {
    case 'open': return 'open';
    case 'closed': return 'closed';
    case 'opening': return 'opening';
    case 'closing': return 'closing';
    case 'stopped': return 'stopped';
    default: return 'unknown';
  }
};

// Componente interno para botones de persiana con lógica avanzada de CoverButton
const AdvancedCoverButtons: React.FC<{
  deviceId: number;
  deviceState: string;
  isLoading: boolean;
  online?: boolean;
  authRequired?: boolean;
  pinRequired?: boolean; // Nuevo prop para verificar PIN
  device?: any; // Nuevo prop para tener acceso al dispositivo completo
  onControlCover?: (deviceId: number, action: 'up' | 'down' | 'stop') => void;
  websocketStatus?: 'connected' | 'disconnected' | 'connecting';
  isDemo?: boolean; // Prop para verificar si el usuario es Demo
  onControlBlocked?: () => boolean;
}> = ({ deviceId, deviceState, isLoading, online = true, authRequired = false, pinRequired = false, device, onControlCover, websocketStatus = 'connected', isDemo = false, onControlBlocked }) => {
  // Estado para controlar la animación
  const [isMoving, setIsMoving] = useState(false);
  const [direction, setDirection] = useState<'up' | 'down' | null>(null);
  
  // Mapear el estado del dispositivo
  const mappedState = mapCoverStateToBackend(deviceState);
  
  // Actualizar el estado local basado en props (se ejecuta cuando cambian props)
  useEffect(() => {
    // Actualizar estado basado en las props recibidas
    if (mappedState === 'opening') {
      setIsMoving(true);
      setDirection('up');
    } else if (mappedState === 'closing') {
      setIsMoving(true);
      setDirection('down');
    } else if (mappedState === 'open' || mappedState === 'closed' || mappedState === 'stopped' || mappedState === 'unknown') {
      setIsMoving(false);
      setDirection(null);
    }
  }, [mappedState]);
  
  // Suscribirse a eventos de actualización de dispositivos
  useEffect(() => {
    // Función para manejar actualizaciones del dispositivo
    const handleDeviceUpdate = (data: any) => {
      if (!data || !data.id) {
        console.warn('[AdvancedCoverButtons] Formato de datos desconocido:', data);
        return;
      }

      if (data.id === deviceId && data.tipo === 'PLUS2PM') {
        const coverState = data.estado || 'stopped';
        const mappedCoverState = mapCoverStateToBackend(coverState);
        
        // Si el dispositivo está en movimiento
        if (mappedCoverState === 'opening') {
          setIsMoving(true);
          setDirection('up');
        } 
        else if (mappedCoverState === 'closing') {
          setIsMoving(true);
          setDirection('down');
        }
        // Si el dispositivo se detuvo
        else if (mappedCoverState === 'stopped' || mappedCoverState === 'open' || mappedCoverState === 'closed') {
          setIsMoving(false);
          setDirection(null);
        }
      }
    };
    
    // Suscribirse a los eventos de actualización
    eventBus.on('device:update', handleDeviceUpdate);
    
    // Limpiar suscripciones
    return () => {
      eventBus.off('device:update', handleDeviceUpdate);
    };
  }, [deviceId]); // Solo dependemos de deviceId para evitar re-suscripciones innecesarias

  // Handler optimizado con useCallback para botón de subir
  const handleUp = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isLoading && onControlCover) {
      if (onControlBlocked?.()) {
        return;
      }
      // Si es demo, llamar a onControlCover para que App.tsx muestre el snackbar
      if (isDemo) {
        onControlCover(deviceId, 'up');
        return;
      }
      // Verificar PIN antes de controlar
      const deviceForPin = device || ((window as any).mapaDispositivos?.[deviceId]);
      if (deviceForPin && (window as any).checkPinRequired && (window as any).checkPinRequired(deviceForPin)) {
        if (!(window as any).isPinSessionValid || !(window as any).isPinSessionValid()) {
          (window as any).requestPinForControl(deviceForPin, 'control_cover', 'up');
          return;
        }
      }
      
      // Usar el estado real del dispositivo para determinar si está en movimiento
      const isCurrentlyMoving = mappedState === 'opening' || mappedState === 'closing';
      const isCurrentlyMovingUp = mappedState === 'opening';
      
      // Si el dispositivo está en movimiento hacia arriba, enviar comando STOP
      if (isCurrentlyMoving && isCurrentlyMovingUp) {
        // Enviar comando de parada
        onControlCover(deviceId, 'stop');
      } else {
        // Establecer estado local inmediatamente para UI responsiva
        setIsMoving(true);
        setDirection('up');
        onControlCover(deviceId, 'up');
      }
      
      // Simulamos la finalización de la animación después de un tiempo, por si la respuesta del servidor se demora
      setTimeout(() => {
        // Solo detenemos si sigue en la misma dirección, comprobando el estado actual 
        setIsMoving((currentIsMoving) => {
          setDirection((currentDirection) => {
            if (currentIsMoving && currentDirection === 'up') {
              return null; // Resetear dirección
            }
            return currentDirection;
          });
          
          if (currentIsMoving && direction === 'up') {
            return false; // Detener movimiento
          }
          return currentIsMoving;
        });
      }, 30000); // 30 segundos como tiempo máximo
    }
  }, [device, deviceId, onControlCover, isLoading, direction, mappedState, isDemo, onControlBlocked]);

  // Handler optimizado con useCallback para botón de bajar
  const handleDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isLoading && onControlCover) {
      if (onControlBlocked?.()) {
        return;
      }
      // Si es demo, llamar a onControlCover para que App.tsx muestre el snackbar
      if (isDemo) {
        onControlCover(deviceId, 'down');
        return;
      }
      // Verificar PIN antes de controlar
      const deviceForPin = device || ((window as any).mapaDispositivos?.[deviceId]);
      if (deviceForPin && (window as any).checkPinRequired && (window as any).checkPinRequired(deviceForPin)) {
        if (!(window as any).isPinSessionValid || !(window as any).isPinSessionValid()) {
          (window as any).requestPinForControl(deviceForPin, 'control_cover', 'down');
          return;
        }
      }
      
      // Usar el estado real del dispositivo para determinar si está en movimiento
      const isCurrentlyMoving = mappedState === 'opening' || mappedState === 'closing';
      const isCurrentlyMovingDown = mappedState === 'closing';
      
      // Si el dispositivo está en movimiento hacia abajo, enviar comando STOP
      if (isCurrentlyMoving && isCurrentlyMovingDown) {
        // Enviar comando de parada
        onControlCover(deviceId, 'stop');
      } else {
        // Establecer estado local inmediatamente para UI responsiva
        setIsMoving(true);
        setDirection('down');
        onControlCover(deviceId, 'down');
      }
      
      // Simulamos la finalización de la animación después de un tiempo, por si la respuesta del servidor se demora
      setTimeout(() => {
        // Solo detenemos si sigue en la misma dirección, comprobando el estado actual
        setIsMoving((currentIsMoving) => {
          setDirection((currentDirection) => {
            if (currentIsMoving && currentDirection === 'down') {
              return null; // Resetear dirección
            }
            return currentDirection;
          });
          
          if (currentIsMoving && direction === 'down') {
            return false; // Detener movimiento
          }
          return currentIsMoving;
        });
      }, 30000); // 30 segundos como tiempo máximo
    }
  }, [device, deviceId, onControlCover, isLoading, direction, mappedState, isDemo, onControlBlocked]);

  // Verificar si el WebSocket no está conectado (mostrar animación de reconexión)
  const isWebSocketDisconnected = websocketStatus !== 'connected';

  return (
    <Box sx={{ 
      position: 'relative',
      width: POWER_BUTTON_SIZE * 2.2 * 1.1, // Mantener tamaño actual de DeviceCard
      height: (POWER_BUTTON_SIZE + 3.5) * 1.1, // Mantener tamaño actual de DeviceCard
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {/* Si WebSocket no está conectado, mostrar animación de reconexión */}
      {isWebSocketDisconnected ? (
        <ReconnectingDots size={6} color="#2391FF" spacing={4} />
      ) : (
        <>
          {/* Indicador de carga */}
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex: 2,
                overflow: 'visible',
              }}
            >
              <CircularProgress
                size={POWER_BUTTON_SIZE + 8}
                sx={{ color: '#2391FF', flexShrink: 0 }}
              />
            </Box>
          )}
          
          {/* Marco ovalado que cambia a azul durante la animación - Con borde gris claro y más grueso */}
          <Box
            sx={{
              position: 'absolute',
              width: 'calc(100% + 4.5px)', // Mantener estiramiento actual de DeviceCard
              height: 'calc(100% + 4.5px)', // Mantener estiramiento actual de DeviceCard
              top: '-2.25px', // Mantener posición actual de DeviceCard
              left: '-2.25px', // Mantener posición actual de DeviceCard
              borderRadius: '35px', // Mantener redondeado actual de DeviceCard
              border: isMoving 
                ? '6px solid #2391FF'
                : '6px solid #4a4a4a',
              boxShadow: isMoving ? '0 0 8px rgba(35, 145, 255, 0.5)' : 'none',
              transition: `border ${ANIMATION_DURATION}s ease-in-out, box-shadow ${ANIMATION_DURATION}s ease-in-out`,
              opacity: isLoading ? 0.5 : 1,
              boxSizing: 'border-box',
            }}
          />
          
          {/* Botón de subir - Mantener posicionamiento actual de DeviceCard */}
          <IconButton
            onClick={handleUp}
            disabled={isLoading || !online || authRequired || isDemo}
            disableRipple
            size="small"
            sx={{
              position: 'absolute',
              left: '4.5px',
              top: '4px',
              width: (POWER_BUTTON_SIZE - 4) * 1.1, // Mantener tamaño actual de DeviceCard
              height: (POWER_BUTTON_SIZE - 4) * 1.1, // Mantener tamaño actual de DeviceCard
              borderRadius: '50%',
              backgroundColor: isMoving ? 'white' : 'transparent',
              border: '2px solid white',
              '&:hover': {
                backgroundColor: isMoving ? '#f5f5f5' : 'rgba(255, 255, 255, 0.1)',
              },
              p: 0,
              boxShadow: 'none',
              transition: `background-color ${ANIMATION_DURATION}s ease-in-out`,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            {isMoving && direction === 'up' ? (
              <Pause
                sx={{
                  fontSize: '1.2rem',
                  color: '#2391FF',
                }}
              />
            ) : (
              <ArrowUpward
                sx={{
                  fontSize: isMoving ? '1.3rem' : '1.2rem',
                  color: isMoving ? '#2391FF' : 'white',
                  fontWeight: isMoving ? 900 : 'normal',
                }}
              />
            )}
          </IconButton>
          
          {/* Botón de bajar - Mantener posicionamiento actual de DeviceCard */}
          <IconButton
            onClick={handleDown}
            disabled={isLoading || !online || authRequired || isDemo}
            disableRipple
            size="small"
            sx={{
              position: 'absolute',
              right: '4.5px',
              top: '4px',
              width: (POWER_BUTTON_SIZE - 4) * 1.1, // Mantener tamaño actual de DeviceCard
              height: (POWER_BUTTON_SIZE - 4) * 1.1, // Mantener tamaño actual de DeviceCard
              borderRadius: '50%',
              backgroundColor: isMoving ? 'white' : 'transparent',
              border: '2px solid white',
              '&:hover': {
                backgroundColor: isMoving ? '#f5f5f5' : 'rgba(255, 255, 255, 0.1)',
              },
              p: 0,
              boxShadow: 'none',
              transition: `background-color ${ANIMATION_DURATION}s ease-in-out`,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            {isMoving && direction === 'down' ? (
              <Pause
                sx={{
                  fontSize: '1.2rem',
                  color: '#2391FF',
                }}
              />
            ) : (
              <ArrowDownward
                sx={{
                  fontSize: isMoving ? '1.3rem' : '1.2rem',
                  color: isMoving ? '#2391FF' : 'white',
                  fontWeight: isMoving ? 900 : 'normal',
                }}
              />
            )}
          </IconButton>
        </>
      )}
    </Box>
  );
};



// Interfaz para las props del componente DeviceCard
interface DeviceCardProps {
  device: any;
  userRole?: string;
  deviceErrors?: Set<number>;
  confirmingDevices?: Set<number>;
  confirmedDevices?: Set<number>;
  coverActions?: Record<number, string>;
  onDeviceClick?: (device: any) => void;
  onDeviceToggle?: (device: any) => void;
  onControlCover?: (deviceId: number, action: 'up' | 'down' | 'stop') => void;
  onDeviceImageClick?: (ip: string) => void;
  // Nuevas props para timer
  deviceTimerStates?: Record<number, {
    isTimerActive: boolean;
    timerStartTime?: number;
  }>;
  // Props para modo edición
  isEditing?: boolean;
  isSelected?: boolean;
  onSelect?: (deviceId: number) => void;
  // Prop para modo de edición de habitación (reorganización)
  isRoomEditing?: boolean;
  // Props para drag & drop
  dragHandleProps?: any;
  // Props para edición de nombre
  isEditingName?: boolean;
  onStartEditName?: () => void;
  onSaveEditName?: (newName: string) => void;
  onCancelEditName?: () => void;
  // Prop para ancho personalizado (útil para modales en layout horizontal)
  customWidth?: string | number;
  // Prop para verificar si el usuario es Demo
  isDemo?: boolean;
  // Callback para mostrar snackbar de restricción demo
  onDemoRestriction?: () => void;
}

// Función para verificar si el timer tiene configuración real (no por defecto)
const hasTimerConfig = (timerConfig: any): boolean => {
  if (!timerConfig) return false;
  return timerConfig.autoOnEnabled || timerConfig.autoOffEnabled || timerConfig.oneTimeOnly;
};

// Función para verificar si la agenda tiene configuración real (no por defecto)
const hasAgendaConfig = (agendaConfig: any): boolean => {
  if (!agendaConfig) return false;
  if (Array.isArray(agendaConfig.jobs) && agendaConfig.jobs.length > 0) return true;
  if (agendaConfig.selectedDays?.length > 0) return true;
  if (agendaConfig.scheduleTimes?.length > 1) return true;
  if (agendaConfig.scheduleTimes?.length === 1) {
    const time = agendaConfig.scheduleTimes[0];
    if (time.hours !== 0 || time.minutes !== 0 || time.seconds !== 0) return true;
  }
  if (agendaConfig.actionType || agendaConfig.toggleEnabled) return true;
  return false;
};

/** Tooltip táctil/hover para badges de la tarjeta (PIN, Timer, etc.). */
const DeviceBadgeTooltip: React.FC<{
  title: React.ReactNode;
  children: React.ReactElement;
}> = ({ title, children }) => (
  <Tooltip
    title={
      typeof title === 'string' ? (
        <Box component="span" sx={{ whiteSpace: 'pre-line', display: 'block' }}>
          {title}
        </Box>
      ) : (
        title
      )
    }
    arrow
    enterDelay={400}
    enterTouchDelay={0}
    leaveTouchDelay={2800}
    describeChild
  >
    <Box
      component="span"
      role="img"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onTouchEnd={(e) => {
        e.stopPropagation();
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        pointerEvents: 'auto',
        touchAction: 'manipulation',
        cursor: 'help',
        lineHeight: 0,
      }}
    >
      {children}
    </Box>
  </Tooltip>
);

// Componente principal DeviceCard (código copiado exacto de App.tsx líneas 2523-2817)
const DeviceCard: React.FC<DeviceCardProps> = ({
  device,
  userRole = 'user',
  deviceErrors = new Set(),
  confirmingDevices = new Set(),
  confirmedDevices = new Set(),
  coverActions = {},
  onDeviceClick,
  onDeviceToggle,
  onControlCover,
  onDeviceImageClick,
  // Nuevas props para timer
  deviceTimerStates = {},
  // Props para modo edición
  isEditing = false,
  isSelected = false,
  onSelect,
  // Prop para modo de edición de habitación
  isRoomEditing = false,
  // Props para drag & drop
  dragHandleProps,
  // Props para edición de nombre
  isEditingName = false,
  onStartEditName,
  onSaveEditName,
  onCancelEditName,
  // Prop para ancho personalizado
  customWidth,
  // Prop para verificar si el usuario es Demo
  isDemo = false,
  // Callback para mostrar snackbar de restricción demo
  onDemoRestriction,
}) => {
  const { t } = useTranslation();
  const [permissionSnackbar, setPermissionSnackbar] = useState({ open: false, message: '' });

  const blockIfCannotControl = useCallback((): boolean => {
    if (isDemo) {
      if (onDemoRestriction) {
        onDemoRestriction();
      } else {
        setPermissionSnackbar({ open: true, message: t('permissions.demoRestriction') });
      }
      return true;
    }
    const mapaDevice = (window as any).mapaDispositivos?.[device.id];
    const roomId = device.habitacion_id ?? mapaDevice?.habitacion_id;
    if (getCanControlDevicesCached(roomId) === false) {
      setPermissionSnackbar({ open: true, message: t('permissions.noControlDevices') });
      return true;
    }
    return false;
  }, [device, isDemo, onDemoRestriction, t]);
  const aiPolicy = useAiDevicePolicy();
  const showAiCardBadge = useShowAiCardBadge();
  const aiWhitelisted = showAiCardBadge && aiPolicy.isWhitelistedForIcon(device.id);
  const aiBlacklisted = showAiCardBadge && aiPolicy.isBlacklistedForIcon(device.id);
  
  // Estado local para el nombre durante la edición
  const [editingNameValue, setEditingNameValue] = useState(device.nombre);
  
  // Actualizar el valor de edición cuando entra en modo edición
  useEffect(() => {
    if (isEditingName) {
      // Obtener el nombre más actualizado desde mapaDispositivos o device
      const mapaDevice = (window as any).mapaDispositivos?.[device.id];
      const currentName = mapaDevice?.nombre || device.nombre || '';
      setEditingNameValue(currentName);
    }
  }, [isEditingName, device.id, device.nombre]);

  // CRÍTICO: Permitir que eventos wheel pasen al contenedor scrollable padre
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    const handleWheel = (e: WheelEvent) => {
      // Encontrar el contenedor scrollable padre (dashboardContainerRef)
      // Buscar hacia arriba en el DOM hasta encontrar un contenedor con scroll
      let parent = overlay.parentElement;
      let foundContainer = false;
      
      while (parent && !foundContainer) {
        // Buscar el contenedor con overflowY auto o scroll
        const style = window.getComputedStyle(parent);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          // Disparar el evento wheel en el contenedor padre
          // Usar el método scrollBy para hacer scroll directamente
          parent.scrollBy({
            top: e.deltaY,
            left: e.deltaX,
            behavior: 'auto'
          });
          foundContainer = true;
          // También disparar el evento para que otros listeners lo capturen
          parent.dispatchEvent(new WheelEvent('wheel', {
            deltaY: e.deltaY,
            deltaX: e.deltaX,
            deltaZ: e.deltaZ,
            bubbles: true,
            cancelable: true,
          }));
          break;
        }
        parent = parent.parentElement;
      }
    };

    overlay.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      overlay.removeEventListener('wheel', handleWheel);
    };
  }, []);
  
  // Estado local para el estado del dispositivo (actualizado en tiempo real desde mapaDispositivos)
  const [evohomeRev, setEvohomeRev] = useState(0);
  const [deviceState, setDeviceState] = useState(() => {
    // Obtener estado inicial desde mapaDispositivos si está disponible, sino desde device
    const mapaDevice = (window as any).mapaDispositivos?.[device.id];
    const deviceData = mapaDevice || device;
    return deviceData.estado;
  });
  
  const overlayRef = useRef<HTMLDivElement | null>(null);
  
  // Estado local para tipo_artefacto para forzar re-render cuando cambie
  const [tipoArtefacto, setTipoArtefacto] = useState(() => {
    const mapaDevice = (window as any).mapaDispositivos?.[device.id];
    return device.tipo_artefacto || mapaDevice?.tipo_artefacto || mapaDispositivos[device.id]?.tipo_artefacto || null;
  });
  
  // Estado del WebSocket para mostrar animación de reconexión
  const [websocketStatus, setWebsocketStatus] = useState<'connected' | 'disconnected' | 'connecting'>(() => {
    // Verificar estado inicial del WebSocket
    const globalStatus = (window as any).websocketStatus;
    if (globalStatus) {
      console.log('[DeviceCard] Estado inicial desde global:', globalStatus, 'deviceId:', device.id);
      return globalStatus;
    }
    if (typeof (window as any).WebSocketService !== 'undefined' && (window as any).WebSocketService.isConnected()) {
      console.log('[DeviceCard] Estado inicial: connected (desde WebSocketService)', 'deviceId:', device.id);
      return 'connected';
    }
    console.log('[DeviceCard] Estado inicial: disconnected', 'deviceId:', device.id);
    return 'disconnected';
  });
  
  // Actualizar el valor del nombre cuando cambie el dispositivo
  useEffect(() => {
    setEditingNameValue(device.nombre);
  }, [device.nombre]);
  
  // Escuchar eventos de WebSocket para actualizar estado
  useEffect(() => {
    let currentStatus: 'connected' | 'disconnected' | 'connecting' = 'disconnected';
    
    // Inicializar estado desde el estado global de App.tsx si está disponible
    if (typeof (window as any).websocketStatus !== 'undefined') {
      currentStatus = (window as any).websocketStatus;
      setWebsocketStatus(currentStatus);
    } else if (typeof (window as any).WebSocketService !== 'undefined' && (window as any).WebSocketService.isConnected()) {
      currentStatus = 'connected';
      setWebsocketStatus('connected');
    }
    
    // SOLUCIÓN: 'websocket:reconnected' ahora marca como 'connecting' (no 'connected')
    // Solo 'websocket:ready' marca como 'connected'
    const handleWebSocketReconnected = () => {
      // 'websocket:reconnected' se emite cuando el socket se reconecta, pero aún no está listo
      // Marcar como 'connecting' para mostrar moving dots
      currentStatus = 'connecting';
      setWebsocketStatus('connecting');
    };
    
    const handleWebSocketReady = () => {
      // 'websocket:ready' se emite cuando TODO está realmente listo
      currentStatus = 'connected';
      setWebsocketStatus('connected');
    };
    
    const handleWebSocketDisconnected = () => {
      currentStatus = 'disconnected';
      setWebsocketStatus('disconnected');
    };
    
    const handleWebSocketConnecting = () => {
      currentStatus = 'connecting';
      setWebsocketStatus('connecting');
    };
    
    eventBus.on('websocket:reconnected', handleWebSocketReconnected);
    eventBus.on('websocket:ready', handleWebSocketReady);
    eventBus.on('websocket:disconnected', handleWebSocketDisconnected);
    eventBus.on('websocket:connecting', handleWebSocketConnecting);
    // Sin setInterval por tarjeta: con todas las habitaciones montadas el poll de 200ms
    // saturaba el hilo y trababa el scroll. Los eventos de arriba bastan.
    
    return () => {
      eventBus.off('websocket:reconnected', handleWebSocketReconnected);
      eventBus.off('websocket:ready', handleWebSocketReady);
      eventBus.off('websocket:disconnected', handleWebSocketDisconnected);
      eventBus.off('websocket:connecting', handleWebSocketConnecting);
    };
  }, []);
  
  // Escuchar eventos de actualización de dispositivos para actualizar estado en tiempo real
  useEffect(() => {
    const handleDeviceUpdate = (data: any) => {
      if (!data) return;
      // Evitar setState en masa mientras el usuario scrollea la lista completa.
      if (isUiScrolling()) return;
      
      // Manejar evento device:artifact_type_updated que viene con deviceIds (array)
      if (data.deviceIds && Array.isArray(data.deviceIds)) {
        if (data.deviceIds.includes(device.id)) {
          const mapaDevice = (window as any).mapaDispositivos?.[device.id];
          const nuevoTipoArtefacto = data.tipoArtefacto || mapaDevice?.tipo_artefacto || mapaDispositivos[device.id]?.tipo_artefacto || null;
          setTipoArtefacto(nuevoTipoArtefacto);
        }
        return;
      }
      
      // Manejar eventos device:update que vienen con id individual
      if (!data.id || data.id !== device.id) {
        return;
      }
      
      // Actualizar estado desde mapaDispositivos (fuente de verdad actualizada)
      // CRÍTICO: El estado SIEMPRE debe venir de mapaDispositivos, nunca del evento directamente
      // CORRECCIÓN: Usar función de actualización para evitar closure stale
      const mapaDevice = (window as any).mapaDispositivos?.[device.id];
      
      if (mapaDevice && mapaDevice.estado !== undefined) {
        const estadoNuevo = mapaDevice.estado;
        // CORRECCIÓN: Usar función de actualización para leer el estado actual y evitar closure stale
        setDeviceState((prevState: boolean | undefined) => {
          return estadoNuevo;
        });
      }
      // Eliminado fallback manual: el estado siempre debe venir de mapaDispositivos
      
      // Actualizar tipo_artefacto si cambió
      if (data.tipo_artefacto !== undefined || data.artefacto_id !== undefined) {
        const nuevoTipoArtefacto = data.tipo_artefacto || mapaDevice?.tipo_artefacto || mapaDispositivos[device.id]?.tipo_artefacto || null;
        setTipoArtefacto(nuevoTipoArtefacto);
      }
    };
    
    // ✅ FIX MEMORY LEAK: Actualizar inicialmente desde mapaDispositivos al montar
    // Las actualizaciones en tiempo real vienen del eventBus (fuente principal)
    // El registro global proporciona backup periódico cada 2 segundos
    const updateStateFromMap = () => {
      if (isUiScrolling()) return;
      const mapaDevice = (window as any).mapaDispositivos?.[device.id];
      if (mapaDevice) {
        if (mapaDevice.estado !== undefined) {
          setDeviceState((prevState: boolean | undefined) => {
            if (prevState === mapaDevice.estado) return prevState;
            return mapaDevice.estado;
          });
        }
        // Actualizar tipo_artefacto desde mapaDispositivos
        if (mapaDevice.tipo_artefacto !== undefined) {
          setTipoArtefacto((prev: string | null) =>
            prev === mapaDevice.tipo_artefacto ? prev : mapaDevice.tipo_artefacto,
          );
        }
      }
    };
    
    // Actualizar inicialmente al montar
    updateStateFromMap();
    
    // Registrar en el sistema global para recibir actualizaciones periódicas de backup
    const componentId = `DeviceCard-${device.id}`;
    registerComponent(componentId, 'DeviceCard', updateStateFromMap);
    
    // Escuchar eventos en tiempo real (fuente principal de actualizaciones)
    // CORRECCIÓN PROBLEMA 2: NO escuchar 'device_control_response' aquí
    // Solo escuchar 'device:update' que ya viene con el estado corregido desde handleDeviceUpdate
    // Esto evita que DeviceCard procese eventos duplicados que causan doble encendido/apagado
    eventBus.on('device:update', handleDeviceUpdate);
    eventBus.on('dispositivo:actualizado', handleDeviceUpdate);
    eventBus.on('device:artifact_type_updated', handleDeviceUpdate);
    
    return () => {
      // Desregistrar del sistema global al desmontar
      unregisterComponent(componentId);
      eventBus.off('device:update', handleDeviceUpdate);
      eventBus.off('dispositivo:actualizado', handleDeviceUpdate);
      eventBus.off('device:artifact_type_updated', handleDeviceUpdate);
    };
  }, [device.id]);

  useEffect(() => eventBus.on('evohome:updated', () => {
    setEvohomeRev((n) => n + 1);
  }), []);

  // MEJORA: Pre-inicializar listeners si el dispositivo tiene cámaras asociadas
  // Esto asegura que los botones estén listos inmediatamente cuando se abre la ventana flotante
  useEffect(() => {
    // Verificar si el dispositivo tiene cámaras asociadas
    const mapaDevice = (window as any).mapaDispositivos?.[device.id];
    const deviceWithCameras = mapaDevice || device;
    const hasCameras = deviceWithCameras.camera_ids && 
                       Array.isArray(deviceWithCameras.camera_ids) && 
                       deviceWithCameras.camera_ids.length > 0;
    
    if (hasCameras) {
      // Pre-inicializar pool de listeners inmediatamente
      getListenerPool();
      
      // Pre-verificar y configurar WebSocket si está disponible
      if (typeof (window as any).WebSocketService !== 'undefined') {
        const wsService = (window as any).WebSocketService;
        if (!wsService.isConnected()) {
          const token = localStorage.getItem('token');
          if (token) {
            wsService.connect(token);
          }
        }
        // Asegurar que el listener de respuestas esté configurado
        wsService.setupControlResponseListener();
      }
    }
  }, [device]); // device: fallback mapaDispositivos || device y camera_ids
  
  const isOnline = device.online !== false;
  
  // CRITICO: DOBLE VERIFICACION de pin_required y auth_required
  // Primero verificar desde mapaDispositivos (fuente de verdad más actualizada)
  const mapaDevice = (window as any).mapaDispositivos?.[device.id];
  const pinRequiredFromMapa = mapaDevice?.pin_required ?? false;
  const authRequiredFromMapa = mapaDevice?.auth_required ?? false;
  
  // CRITICO: Verificar si mapaDispositivos está cargado usando la función exportada
  const isMapaCargado = (window as any).isMapaDispositivosCargado ? (window as any).isMapaDispositivosCargado() : false;
  
  // CRITICO: Si mapaDispositivos NO está cargado, usar el valor del device pero con verificación conservadora
  // Si el device no tiene pin_required explícito, asumir que requiere PIN por seguridad durante la carga inicial
  let pinRequired: boolean;
  let authRequired: boolean;
  
  if (isMapaCargado && mapaDevice) {
    // Si mapaDispositivos está cargado y tiene el dispositivo, usar ese valor (más confiable)
    pinRequired = Boolean(pinRequiredFromMapa);
    authRequired = Boolean(authRequiredFromMapa);
  } else {
    // Si mapaDispositivos NO está cargado, usar el valor del device
    // CRITICO: Si el device no tiene pin_required explícito, asumir que requiere PIN por seguridad
    // Esto previene que dispositivos se muestren sin candadito durante la carga inicial
    const devicePinRequired = Boolean(device.pin_required);
    const deviceAuthRequired = Boolean(device.auth_required);
    
    if (!isMapaCargado && !devicePinRequired) {
      // Durante la carga inicial, si no estamos seguros, requerir PIN por seguridad
      // Esto previene race conditions donde dispositivos se muestren sin candadito
      pinRequired = true; // Mejor prevenir que curar
    } else {
      pinRequired = devicePinRequired;
    }
    
    authRequired = deviceAuthRequired;
    
    // Si hay un valor en mapaDispositivos aunque no esté completamente cargado, usarlo como respaldo
    if (pinRequiredFromMapa) {
      pinRequired = true;
    }
    if (authRequiredFromMapa) {
      authRequired = true;
    }
  }
  
  const isAuthRequired = authRequired;  // NUEVO: Detectar si requiere autenticación
  
  // Calcular isUnknown: true si está offline O si requiere autenticación
  const isUnknown = !isOnline || isAuthRequired;
  
  // LOG DETALLADO: Rastrear valores de pin_required para debugging
  console.log(`[DeviceCard-RENDER] Dispositivo ID ${device.id} "${device.nombre}": pinRequired=${pinRequired}, pinRequiredFromMapa=${pinRequiredFromMapa}, device.pin_required=${device.pin_required}, isMapaCargado=${isMapaCargado}, mapaDevice existe=${!!mapaDevice}`);
  
  // CRITICO: Log de depuración si hay discrepancia (solo en desarrollo)
  if (process.env.NODE_ENV === 'development' && pinRequiredFromMapa !== Boolean(device.pin_required)) {
    console.warn(`[DeviceCard] Discrepancia en pin_required para dispositivo ${device.id}: mapaDispositivos=${pinRequiredFromMapa}, device=${device.pin_required}, isMapaCargado=${isMapaCargado}`);
  }
  
  // LÓGICA ESPECIAL PARA PLUS1: Si está en confirmedDevices, mostrar como encendido
  const isPlus1Confirmed = device.tipo === 'PLUS1' && confirmedDevices.has(device.id);
  
  // LÓGICA ESPECÍFICA PARA DIMMER2: Manejar tanto estado boolean como numérico
  // Usar deviceState (actualizado en tiempo real) en lugar de device.estado
  let isOn = device.tipo === 'DIMMER2' 
    ? (deviceState === true || (typeof deviceState === 'number' && deviceState > 0))
    : deviceState === true;
  
  // Override para PLUS1: Si está confirmado, forzar isOn = true
  if (isPlus1Confirmed) {
    isOn = true;
  }
  
  const isCoverDevice = device.tipo === 'PLUS2PM';
  /** Espacio reservado a la derecha por `AdvancedCoverButtons` + `right: 10px` del contenedor de controles. */
  const coverAiBadgeRightPx =
    10 + Math.ceil(POWER_BUTTON_SIZE * 2.2 * 1.1) + 8;

  // Verificar si debe mostrar iconos: si tiene config real O si el master switch está habilitado
  const showTimer = (device.timer_config && hasTimerConfig(device.timer_config)) || (device.timer_config && device.timer_config.enabled);
  const showAgenda = (device.agenda_config && hasAgendaConfig(device.agenda_config)) || (device.agenda_config && device.agenda_config.enabled);

  const timerBadgeTooltip = showTimer
    ? formatTimerBadgeTooltip(device.timer_config, {
        title: t('deviceList.badges.timer'),
        titleOff: t('deviceList.badges.timerOff'),
        autoOn: t('deviceList.badges.autoOn'),
        autoOff: t('deviceList.badges.autoOff'),
        oneTime: t('deviceList.badges.oneTimeOnly'),
        disabled: t('deviceList.badges.disabled'),
      })
    : '';
  const agendaBadgeTooltip = showAgenda
    ? (device.agenda_config?.enabled
        ? t('deviceList.badges.agenda')
        : t('deviceList.badges.agendaOff'))
    : '';
  
  // Verificar si tiene cámaras asignadas (similar a como se verifica PIN)
  const mapaDeviceForCameras = (window as any).mapaDispositivos?.[device.id];
  const deviceWithCameras = mapaDeviceForCameras || device;
  const hasCameras = deviceWithCameras.camera_ids && 
                     Array.isArray(deviceWithCameras.camera_ids) && 
                     deviceWithCameras.camera_ids.length > 0;
  
  // TimerCountdown: auto-off en ON, auto-on en OFF (misma regla que PowerButton)
  const timerConfigForCountdown = device.timer_config;
  const isTimerActiveForCountdown = deviceTimerStates[device.id]?.isTimerActive || false;
  const timerStartTimeForCountdown = deviceTimerStates[device.id]?.timerStartTime;
  const deviceIsOnForTimer = isOn === true;
  const shouldShowTimerCountdown = shouldShowDeviceTimer(
    isTimerActiveForCountdown,
    timerConfigForCountdown,
    deviceIsOnForTimer
  );
  
  const timerProgress = useTimerProgress({
    timerConfig: timerConfigForCountdown || {
      enabled: false,
      autoOnEnabled: false,
      autoOffEnabled: false,
      oneTimeOnly: false,
      autoOnHours: 0,
      autoOnMinutes: 0,
      autoOnSeconds: 0,
      autoOffHours: 0,
      autoOffMinutes: 0,
      autoOffSeconds: 0
    },
    isActive: shouldShowTimerCountdown,
    startTime: timerStartTimeForCountdown,
    deviceIsOn: deviceIsOnForTimer
  });

  return (
      <Box
        component="div"
        data-device-id={device.id}
        // CRÍTICO: NO usar onClick aquí - interfiere con el scroll en móviles
        // El navegador móvil espera a determinar si es un tap antes de iniciar el scroll cuando detecta onClick
        // El tap se detectará completamente desde el contenedor scrollable usando onTouchEnd
        // Esto permite que el scroll funcione desde cualquier parte sin interferencia
        sx={{
          backgroundColor: '#2F3235',
          display: 'flex',
          alignItems: 'center',
          padding: 1.25,
          borderRadius: 2, // Más redondeado como en React Native (8px)
          marginBottom: 0.5, // Mitad de separación vertical (4px)
          height: 64, // Altura fija 64px
          width: '100%', // Ajustar al ancho disponible del grid
          maxWidth: '100%',
          minWidth: 0, // Permitir que el grid encoja la tarjeta sin forzar scroll horizontal
          boxSizing: 'border-box',
          position: 'relative',
          cursor: isEditingName ? 'default' : 'pointer',
          overflow: 'visible', // Permitir que los iconos se vean fuera
          border: isSelected ? '1px solid #2391FF' : 'none', // Borde azul fino cuando seleccionado
          // CRÍTICO: pointer-events: none permite que eventos pasen directamente al contenedor scrollable
          // Esto permite que el scroll funcione desde cualquier parte sin interferencia
          // El tap se detectará usando un overlay invisible que cubre todo el DeviceCard excepto los botones
          pointerEvents: 'none', // CRÍTICO: Permitir que eventos pasen al contenedor scrollable
          // CRÍTICO: Los elementos interactivos dentro deben poder recibir eventos
          '& button, & .MuiIconButton-root, & .MuiButton-root, & [role="button"], & input, & textarea, & img': {
            pointerEvents: 'auto', // Permitir eventos solo en elementos interactivos específicos
            touchAction: 'manipulation', // Permitir interacción en elementos interactivos
          },
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
        }}
      >
      {/* Overlay invisible para detectar taps - cubre todo el DeviceCard excepto icono y botones */}
      {/* Permite scroll mientras detecta taps */}
      {!isEditing && !isEditingName && (
        <Box
          ref={overlayRef}
          onClick={(e) => {
            // Verificar que NO sea un botón ni el icono antes de ejecutar onDeviceClick
            const target = e.target as HTMLElement;
            const isButton = target.closest('button') !== null || 
                             target.closest('.MuiIconButton-root') !== null ||
                             target.closest('.MuiButton-root') !== null;
            // Verificar si el click fue sobre el icono del dispositivo
            // Buscar el elemento con data-device-icon en la jerarquía del DOM
            const iconContainer = target.closest('[data-device-icon]');
            const isIcon = iconContainer !== null;
            
            if (!isButton && !isIcon) {
              // CRÍTICO: Verificar que NO haya habido scroll ANTES de ejecutar el tap
              const dashboardHasScrolled = (window as any).dashboardHasScrolledRef?.current || false;
              if (!dashboardHasScrolled && onDeviceClick) {
                onDeviceClick(device);
              }
            }
          }}
          sx={{
            position: 'absolute',
            top: 0,
            left: '80px', // CRÍTICO: Excluir área del icono para permitir scroll sobre el icono
            right: '120px', // Dejar espacio para botones
            bottom: 0,
            pointerEvents: 'auto', // CRÍTICO: Permitir eventos para detectar taps
            touchAction: 'pan-y', // CRÍTICO: Permitir scroll vertical - esto permite que el scroll funcione
            // CRÍTICO: Permitir que eventos wheel pasen al contenedor scrollable
            // Los eventos wheel deben propagarse al contenedor padre para que el scroll funcione
            '&:hover': {
              // No hacer nada especial en hover - el scroll wheel debería funcionar automáticamente
            },
            zIndex: 1,
            cursor: 'pointer',
          }}
        />
      )}
      {/* NUEVO: Indicador de autenticación requerida */}
      {isAuthRequired && (
        <Box
          sx={{
            position: 'absolute',
            right: '130px', // Movido 50px a la izquierda (era 80px)
            bottom: 6, // Bajado 2px (era 8)
            zIndex: 100
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              color: '#ffa500',
              fontWeight: 'bold',
              backgroundColor: 'rgba(255, 165, 0, 0.1)',
              padding: '0px 4.4px',
              borderRadius: '8.2px', // Mismo borderRadius que cuenta regresiva
              border: '1px solid #ffa500',
              textAlign: 'center',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
              lineHeight: 1.0
            }}
          >
            auth
          </Typography>
        </Box>
      )}
      
      {/* Imagen del dispositivo o íconos de grupo (+ chip de marca en edición Settings) */}
      <DeviceIconWithBrandChip
        brand="shelly"
        showBrandChip={Boolean(isEditing)}
        sx={{ marginRight: 1.5 }}
      >
        <Box 
          sx={{ 
            // CRÍTICO: Permitir que el scroll funcione sobre el contenedor del icono
            touchAction: 'pan-y', // Permitir scroll vertical sobre el icono
            pointerEvents: 'auto', // Permitir eventos para que el icono pueda recibir clicks
          }}
          data-device-icon // Marcar para que el overlay lo excluya
        >
          {device.is_group ? (
            // Mostrar íconos de grupo con feedback visual
            <GroupIconsRow
              groupType={device.device_type_category}
              deviceCount={device.device_count || 0}
              confirmedDeviceIds={device.confirmedDeviceIds || []}
            />
          ) : (
            // Mostrar imagen normal del dispositivo
            <DeviceTypeImage 
              tipo={device.tipo}
              tipo_artefacto={tipoArtefacto || device.tipo_artefacto || mapaDispositivos[device.id]?.tipo_artefacto || null}
              deviceId={device.id}
              deviceIP={device.ip}
              isAdmin={userRole === 'admin'}
              onDeviceClick={onDeviceImageClick}
            />
          )}
        </Box>
      </DeviceIconWithBrandChip>
      
      {/* Información del dispositivo */}
      <Box sx={{ 
        flex: 0.8,
        overflow: 'hidden', // Evita que el contenido se desborde
        minWidth: 0, // Permite que el flex se reduzca
        position: 'relative', // Para posicionar el error absolutamente
        transform: 'translateY(-2px)' // Bajar un poco el contenido para separarlo del borde superior
      }}>
        {/* Nombre del dispositivo o campo de edición */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: 0.5,
            position: 'relative',
            zIndex: isEditingName ? 100 : 'auto'
          }}
        >
          {isEditingName ? (
            // Modo edición de nombre
            <>
              <TextField
                value={editingNameValue}
                onChange={(e) => setEditingNameValue(e.target.value)}
                variant="standard"
                autoFocus
                sx={{
                  flex: 1,
                  '& .MuiInput-root': {
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 'bold',
                  },
                  '& .MuiInput-underline:before': {
                    borderBottomColor: '#666',
                  },
                  '& .MuiInput-underline:after': {
                    borderBottomColor: '#2391FF',
                  },
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && onSaveEditName) {
                    onSaveEditName(editingNameValue);
                  }
                }}
              />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  // Llamar directamente - igual que en el commit anterior
                  if (onSaveEditName) {
                    onSaveEditName(editingNameValue);
                  }
                }}
                sx={{ 
                  padding: 1, 
                  marginLeft: 0.5,
                  minWidth: 32,
                  minHeight: 32,
                  zIndex: 101,
                  position: 'relative',
                  backgroundColor: 'rgba(35, 145, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(35, 145, 255, 0.2)',
                  },
                  '&:active': {
                    backgroundColor: 'rgba(35, 145, 255, 0.3)',
                  }
                }}
              >
                <CheckIcon sx={{ fontSize: 18, color: '#2391FF' }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onCancelEditName) {
                    onCancelEditName();
                  }
                }}
                sx={{ 
                  padding: 1,
                  marginLeft: 0.5,
                  minWidth: 32,
                  minHeight: 32,
                  zIndex: 101,
                  position: 'relative',
                  backgroundColor: 'rgba(255, 68, 68, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 68, 68, 0.2)',
                  },
                  '&:active': {
                    backgroundColor: 'rgba(255, 68, 68, 0.3)',
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: 18, color: '#ff4444' }} />
              </IconButton>
            </>
          ) : (
            // Modo normal
            <>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 'bold',
                  color: '#fff',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {device.nombre}
              </Typography>
              {/* Ícono de lápiz - solo visible en modo edición Y cuando NO está en drag & drop */}
              {isEditing && !isRoomEditing && onStartEditName && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStartEditName();
                  }}
                  sx={{ 
                    padding: 0.25, 
                    marginLeft: 0.5,
                    '&:hover': {
                      backgroundColor: 'rgba(35, 145, 255, 0.1)',
                    }
                  }}
                >
                  <EditIcon sx={{ fontSize: 14, color: '#2391FF' }} />
                </IconButton>
              )}
            </>
          )}
        </Box>
        {/* Consumo y detalles - Solo mostrar para dispositivos normales, NO para grupos */}
        {!device.is_group && (
          <>
            <Box sx={{ display: 'flex', gap: 1.25, marginLeft: '-4.5px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Bolt
                  sx={{
                    fontSize: 12, // Tamaño exacto como en React Native
                    color: getColorForConsumo(device.consumo) // Verde para negativos, azul para positivos
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 12, // Tamaño exacto como en React Native
                    fontWeight: 'bold',
                    marginLeft: 0.5, // Reducido para juntar icono con valor
                    color: getColorForConsumo(device.consumo), // Verde para negativos, azul para positivos
                    whiteSpace: 'nowrap' // Evita que el texto se divida en dos líneas
                  }}
                >
                  {formatConsumption(device.consumo || 0)}
                </Typography>
              </Box>
              {device.tipo === 'DIMMER2' && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Brightness6
                    sx={{
                      fontSize: 12, // Tamaño exacto como en React Native
                      color: '#2391FF'
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 12, // Tamaño exacto como en React Native
                      fontWeight: 'bold',
                      marginLeft: 0.5, // Reducido para juntar icono con valor
                      color: '#2391FF'
                    }}
                  >
                    {device.lights_brightness ?? 0}%
                  </Typography>
                </Box>
              )}
              {device.tipo === 'PLUS2PM' && device.cover_position !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {device.cover_position === 0 ? (
                    <BlindsClosedTwoTone 
                      sx={{ 
                        fontSize: 12,
                        color: '#2391FF'
                      }} 
                    />
                  ) : (
                    <BlindsTwoTone 
                      sx={{ 
                        fontSize: 12,
                        color: '#2391FF'
                      }} 
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 'bold',
                      marginLeft: 0.5,
                      color: '#2391FF'
                    }}
                  >
                    {device.cover_position ?? 0}%
                  </Typography>
                </Box>
              )}
            </Box>
            {/* Tipo de artefacto */}
            {(() => {
              // Intentar obtener tipo_artefacto de múltiples fuentes
              const tipoArtefacto = device.tipo_artefacto || 
                mapaDispositivos[device.id]?.tipo_artefacto || 
                ((window as any).mapaDispositivos?.[device.id]?.tipo_artefacto);
              
              // Si no se encuentra, intentar desde el objeto device completo
              const tipoArtefactoFromDevice = (device as any).tipo_artefacto || 
                (device as any).artefacto_nombre;
              
              const finalTipoArtefacto = tipoArtefacto || tipoArtefactoFromDevice;
              
              return finalTipoArtefacto ? (
                <Typography
                  sx={{
                    fontSize: 10,
                    color: '#cccccc', // Gris claro parecido al blanco pero no blanco
                    marginTop: 0.25,
                    marginLeft: '0px', // Ajustado para evitar solapamiento
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {finalTipoArtefacto}
                </Typography>
              ) : null;
            })()}
          </>
        )}
      </Box>
      
      {/* Controles del dispositivo */}
      {/* Mensaje de error de timeout - DENTRO de la tarjeta */}
      {deviceErrors.has(device.id) && (
        <Box sx={{
          position: 'absolute',
          right: '80px', // Movido 10px a la derecha (era 90px)
          bottom: 6, // Bajado 2px (era 8)
          zIndex: 1000 // Mayor zIndex para estar encima de offline si ambos estuvieran presentes
        }}>
          <Typography
            sx={{
              fontSize: 10, // Mismo tamaño que el mensaje offline
              color: '#ff4444',
              fontWeight: 'bold',
              backgroundColor: 'rgba(255, 68, 68, 0.1)',
              padding: '0px 4.4px', // 10% más grande (4 * 1.10)
              borderRadius: '8.2px', // Mismo borderRadius que cuenta regresiva
              border: '1px solid #ff4444',
              textAlign: 'center',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
              lineHeight: 1.0
            }}
          >
            Sin respuesta
          </Typography>
        </Box>
      )}
      
      {/* Cuenta regresiva del timer - Misma posición exacta que "Sin respuesta" */}
      {!isCoverDevice && (
        <Box sx={{
          position: 'absolute',
          right: '35px', // Movido 10px más a la derecha (era 45px)
          bottom: 1, // Movido 1px hacia arriba (era 0)
          zIndex: 1000 // Mismo zIndex que "Sin respuesta"
        }}>
          <TimerCountdown
            timeRemaining={timerProgress.timeRemaining}
            isActive={shouldShowTimerCountdown && timerProgress.isRunning}
            showIcon={true}
            size="small"
            position="right"
          />
        </Box>
      )}
      
      {/* Controles del dispositivo - Solo mostrar si NO está en modo edición */}
      {/* MEJORA: Verificar que mapaDispositivos esté cargado antes de mostrar botones interactivos */}
      {!isEditing && !isRoomEditing && isMapaCargado && (
        <Box 
          sx={{ 
            position: 'absolute', // Posición absoluta para mantener fijo respecto al borde derecho
            right: '10px', // Posición fija desde el borde derecho del Card (padding: 1.25 = 10px)
            top: '8px', // Alineado con el contenido superior
            zIndex: 1 // Más alto para no interferir con el error
          }}
        >
          {isCoverDevice ? (
            // Botones de persiana (PLUS2PM) - Implementación avanzada con lógica de CoverButton
            <AdvancedCoverButtons
              deviceId={device.id}
              deviceState={device.estado || 'stopped'}
              isLoading={confirmingDevices.has(device.id)}
              online={isOnline}
              authRequired={authRequired}
              pinRequired={pinRequired}
              device={device}
              onControlCover={onControlCover}
              websocketStatus={websocketStatus}
              isDemo={isDemo}
              onControlBlocked={blockIfCannotControl}
            />
          ) : (
            // Botón de poder con animación de timer - Optimizado como DeviceModal
            <PowerButton
              isOn={isOn}
              isUnknown={isUnknown}
              isLoading={confirmingDevices.has(device.id)}
              websocketStatus={websocketStatus}
              onClick={() => {
                if (blockIfCannotControl()) {
                  return;
                }
                // CRITICO: Verificar PIN antes de toggle - usar pinRequired de doble verificación
                // NO usar device.pin_required directamente, usar pinRequired que ya tiene doble verificación
                if (pinRequired) {
                  if (!(window as any).isPinSessionValid || !(window as any).isPinSessionValid()) {
                    const mapaDevice = (window as any).mapaDispositivos?.[device.id];
                    const deviceWithPin = mapaDevice ? { ...device, ...mapaDevice } : device;
                    (window as any).requestPinForControl(deviceWithPin, 'toggle');
                    return;
                  }
                }
                onDeviceToggle?.(device);
              }}
              deviceId={device.id}
              timerConfig={device.timer_config}
              isTimerActive={deviceTimerStates[device.id]?.isTimerActive || false}
              timerStartTime={deviceTimerStates[device.id]?.timerStartTime}
              timerPosition="right"
              isDemo={isDemo}
              onDemoRestriction={onDemoRestriction}
            />
          )}
        </Box>
      )}

      {evohomeRev >= 0 && !isCoverDevice && !isEditing && !isRoomEditing && isMapaCargado && isOn === true && isHeatSupplyFrostHolding(device.id) && (
        <Typography
          sx={{
            position: 'absolute',
            right: 0,
            top: 54,
            width: 88,
            fontSize: 9,
            lineHeight: 1.15,
            color: '#ffb74d',
            textAlign: 'center',
            zIndex: 6,
            pointerEvents: 'none',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {t('shelly.frostNote')}
        </Typography>
      )}
      
      {/* DragIndicator cuando está en modo de edición de habitación */}
      {isRoomEditing && (
        <Box 
          {...dragHandleProps}
          sx={{ 
            marginLeft: 0.5,
            transform: 'translateX(40px)', // Ajustado 2px hacia la izquierda
            flexShrink: 0,
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            cursor: 'grab',
            touchAction: 'none', // CRÍTICO: dnd-kit requiere 'none' en el handle para detectar el arrastre táctil (con 'pan-y' el navegador lo trata como scroll)
            '&:active': {
              cursor: 'grabbing'
            }
          }}
        >
          <DragIndicator 
            sx={{ 
              color: 'white',
              fontSize: '2.25rem',
              pointerEvents: 'none'
            }}
          />
        </Box>
      )}
      
      {/* Indicador offline */}
      {!isOnline && (
        <Box
          sx={{
            position: 'absolute',
            right: '80px', // Movido 10px a la derecha (era 90px)
            bottom: 6, // Bajado 2px (era 8)
            zIndex: 100
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              color: '#ff4444',
              fontWeight: 'bold',
              backgroundColor: 'rgba(255, 68, 68, 0.1)',
              padding: '0px 4.4px',
              borderRadius: '8.2px', // Mismo borderRadius que cuenta regresiva
              border: '1px solid #ff4444',
              textAlign: 'center',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
              lineHeight: 1.0
            }}
          >
            offline
          </Typography>
        </Box>
      )}

      {/* Iconos de configuración (Timer, Agenda, PIN, Cámara, IA) — no cortinas: las cortinas usan fila aparte para IA */}
      {/* CRITICO: Usar pinRequired (que viene de doble verificación) en lugar de device.pin_required */}
      {!isCoverDevice &&
        (pinRequired || showTimer || showAgenda || hasCameras || aiWhitelisted || aiBlacklisted) && (
        <Box
          sx={{
            position: 'absolute',
            right: '76px', // Movido 2px a la derecha (era 78px)
            bottom: 24, // Movido 2px más abajo (era 26)
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '4px',
            zIndex: 5
          }}
        >
          {/* Icono Lock - Solo si requiere PIN - CRITICO: Usar pinRequired de doble verificación */}
          {pinRequired && (
            <DeviceBadgeTooltip title={t('deviceList.badges.pin')}>
              <Lock
                sx={{
                  fontSize: 13,
                  color: '#2391FF'
                }}
              />
            </DeviceBadgeTooltip>
          )}
          
          {/* Icono Timer - Solo si hay configuración real o habilitado */}
          {showTimer && (
            <DeviceBadgeTooltip title={timerBadgeTooltip}>
              <Timer
                sx={{
                  fontSize: 13,
                  color: device.timer_config?.enabled ? '#2391FF' : '#888888'
                }}
              />
            </DeviceBadgeTooltip>
          )}
          
          {/* Icono Agenda - Solo si hay configuración real o habilitado */}
          {showAgenda && (
            <DeviceBadgeTooltip title={agendaBadgeTooltip}>
              <CalendarMonth
                sx={{
                  fontSize: 13,
                  color: device.agenda_config?.enabled ? '#2391FF' : '#888888'
                }}
              />
            </DeviceBadgeTooltip>
          )}
          
          {/* Icono Cámara - Solo si tiene cámaras asignadas */}
          {hasCameras && (
            <DeviceBadgeTooltip title={t('deviceList.badges.camera')}>
              <VideoCameraFront
                sx={{
                  fontSize: 14,
                  color: '#2391FF'
                }}
              />
            </DeviceBadgeTooltip>
          )}

          {aiWhitelisted && (
            <DeviceBadgeTooltip title={t('deviceList.badges.ai')}>
              <Typography
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.62rem',
                  fontWeight: 800,
                  color: '#66bb6a',
                  // Misma altura visual que el ícono de cámara (14) para alinear con Lock/Timer/Camera
                  minHeight: 14,
                  lineHeight: '14px',
                  letterSpacing: '-0.02em',
                  userSelect: 'none',
                }}
                aria-label={t('deviceList.aiPolicyAllowed') as string}
              >
                {t('common.aiBadge')}
              </Typography>
            </DeviceBadgeTooltip>
          )}

          {aiBlacklisted && (
            <DeviceBadgeTooltip title={t('deviceList.badges.aiDenied')}>
              <Typography
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.62rem',
                  fontWeight: 800,
                  color: '#ef5350',
                  minHeight: 14,
                  lineHeight: '14px',
                  letterSpacing: '-0.02em',
                  userSelect: 'none',
                }}
                aria-label={t('deviceList.aiPolicyDenied') as string}
              >
                <Box
                  component="span"
                  sx={{
                    textDecoration: 'line-through',
                    textDecorationColor: '#ef5350',
                    textDecorationThickness: '1.5px',
                  }}
                >
                  {t('common.aiBadge')}
                </Box>
              </Typography>
            </DeviceBadgeTooltip>
          )}
        </Box>
      )}

      {/* IA whitelist en cortinas: fuera de la fila anterior (!isCoverDevice) y a la izquierda de los 2 botones */}
      {isCoverDevice && (aiWhitelisted || aiBlacklisted) && (
        <Box
          sx={{
            position: 'absolute',
            right: `${coverAiBadgeRightPx}px`,
            bottom: 24,
            display: 'flex',
            alignItems: 'center',
            zIndex: 10,
          }}
        >
          {aiWhitelisted && (
          <DeviceBadgeTooltip title={t('deviceList.badges.ai')}>
            <Typography
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.62rem',
                fontWeight: 800,
                color: '#66bb6a',
                minHeight: 14,
                lineHeight: '14px',
                letterSpacing: '-0.02em',
                userSelect: 'none',
                pointerEvents: 'auto',
              }}
              aria-label={t('deviceList.aiPolicyAllowed') as string}
            >
              {t('common.aiBadge')}
            </Typography>
          </DeviceBadgeTooltip>
          )}
          {aiBlacklisted && (
            <DeviceBadgeTooltip title={t('deviceList.badges.aiDenied')}>
              <Typography
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.62rem',
                  fontWeight: 800,
                  color: '#ef5350',
                  minHeight: 14,
                  lineHeight: '14px',
                  letterSpacing: '-0.02em',
                  userSelect: 'none',
                  pointerEvents: 'auto',
                }}
                aria-label={t('deviceList.aiPolicyDenied') as string}
              >
                <Box
                  component="span"
                  sx={{
                    textDecoration: 'line-through',
                    textDecorationColor: '#ef5350',
                    textDecorationThickness: '1.5px',
                  }}
                >
                  {t('common.aiBadge')}
                </Box>
              </Typography>
            </DeviceBadgeTooltip>
          )}
        </Box>
      )}

      {/* Checkbox para modo edición - Solo visible cuando NO está en drag & drop */}
      {isEditing && !isRoomEditing && onSelect && (
        <Checkbox
          checked={isSelected}
          onChange={() => onSelect(device.id)}
          sx={{
            position: 'absolute',
            bottom: '-5px',
            right: '-5px',
            color: 'red',
            '& .MuiSvgIcon-root': {
              color: isSelected ? 'red' : 'red',
            },
            '&.Mui-checked': {
              backgroundColor: 'none',
            },
          }}
        />
      )}
      <Snackbar
        open={permissionSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setPermissionSnackbar({ ...permissionSnackbar, open: false })}
        {...getDemoRestrictionSnackbarProps()}
      >
        <Alert
          onClose={() => setPermissionSnackbar({ ...permissionSnackbar, open: false })}
          severity="error"
          sx={{ width: '100%' }}
        >
          {permissionSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// OPTIMIZACIÓN 4 y 7: Memoizar DeviceCard con comparación optimizada para evitar re-renders innecesarios
export default React.memo(DeviceCard, (prevProps, nextProps) => {
  // OPTIMIZADO: Comparación rápida por ID primero
  if (prevProps.device.id !== nextProps.device.id) return false;
  
  // OPTIMIZADO: Comparar propiedades críticas del dispositivo en orden de frecuencia de cambio
  if (prevProps.device.online !== nextProps.device.online) return false;
  if (prevProps.device.estado !== nextProps.device.estado) return false;
  // Badges (PIN, auth, cámaras, timer, agenda): deben forzar re-render al asignar/quitar en Settings
  if (deviceBadgesChanged(prevProps.device, nextProps.device)) return false;
  
  // OPTIMIZADO: Comparar consumo con tolerancia mejorada (redondear a 1 decimal)
  const prevConsumo = Math.round((prevProps.device.consumo || 0) * 10) / 10;
  const nextConsumo = Math.round((nextProps.device.consumo || 0) * 10) / 10;
  if (Math.abs(prevConsumo - nextConsumo) >= 0.1) return false;
  
  // Comparar lights_brightness para DIMMER2
  if (prevProps.device.tipo === 'DIMMER2' && prevProps.device.lights_brightness !== nextProps.device.lights_brightness) return false;
  if (prevProps.device.cover_position !== nextProps.device.cover_position) return false;
  if (prevProps.device.pct_level !== nextProps.device.pct_level) return false;
  
  // OPTIMIZADO: Comparar metadata.coverState si existe (más eficiente que comparar todo el objeto)
  const prevCoverState = prevProps.device.metadata?.coverState;
  const nextCoverState = nextProps.device.metadata?.coverState;
  if (prevCoverState !== nextCoverState) return false;
  
  // Comparar props de estado (menos frecuentes, al final)
  if (prevProps.isEditing !== nextProps.isEditing) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.isEditingName !== nextProps.isEditingName) return false;
  if (prevProps.isRoomEditing !== nextProps.isRoomEditing) return false;
  
  // OPTIMIZADO: Comparar Sets de forma más eficiente (solo verificar si el ID está presente)
  const deviceId = prevProps.device.id;
  const prevHasError = prevProps.deviceErrors?.has(deviceId) ?? false;
  const nextHasError = nextProps.deviceErrors?.has(deviceId) ?? false;
  if (prevHasError !== nextHasError) return false;
  
  const prevIsConfirming = prevProps.confirmingDevices?.has(deviceId) ?? false;
  const nextIsConfirming = nextProps.confirmingDevices?.has(deviceId) ?? false;
  if (prevIsConfirming !== nextIsConfirming) return false;
  
  const prevIsConfirmed = prevProps.confirmedDevices?.has(deviceId) ?? false;
  const nextIsConfirmed = nextProps.confirmedDevices?.has(deviceId) ?? false;
  if (prevIsConfirmed !== nextIsConfirmed) return false;
  
  // Comparar coverActions
  const prevCoverAction = prevProps.coverActions?.[deviceId];
  const nextCoverAction = nextProps.coverActions?.[deviceId];
  if (prevCoverAction !== nextCoverAction) return false;
  
  // Comparar deviceTimerStates
  const prevTimerState = prevProps.deviceTimerStates?.[deviceId];
  const nextTimerState = nextProps.deviceTimerStates?.[deviceId];
  if (prevTimerState?.isTimerActive !== nextTimerState?.isTimerActive) return false;
  if (prevTimerState?.timerStartTime !== nextTimerState?.timerStartTime) return false;
  
  // CRITICO: Comparar onDeviceClick para asegurar que los event handlers se actualicen correctamente
  // Si onDeviceClick cambia de referencia, forzar re-render para actualizar el onClick del Card
  if (prevProps.onDeviceClick !== nextProps.onDeviceClick) return false;

  // Si todas las comparaciones pasan, no hay cambios (evitar re-render)
  return true;
});
